<template>
  <form class="d-leaderboard-feed-search">
    <button
      class="hidden-submit-button"
      type="submit"
    />

    <DuiDropdown
      ref="sortByDropdown"
      v-model="search.sortBy"
      :disabled="isProcessing"
      label="Sort by"
      :label-width="64"
      name="sortBy"
      @change="searchParamUpdated('sort_by')"
    >
      <template slot="options">
        <DuiOption
          text="Month"
          value="month"
        />
        <DuiOption
          text="Year"
          value="year"
        />
        <DuiOption
          text="Overall"
          value="overall"
        />
      </template>
    </DuiDropdown>

    <DuiDatepicker
      v-show="search.sortBy !== 'overall'"
      v-model="search.date"
      class="space-top-2em"
      name="date"
      :clear-button="false"
      :disabled-dates="dateConfig.disabledDates"
      :display-format="dateConfig.displayFormat"
      :format="dateConfig.format"
      :min-view="search.sortBy"
      max-view="year"
      :open-date="dateConfig.openDate"
      :placeholder="dateConfig.placeholder"
      @change="searchParamUpdated('date')"
    />

    <DuiExpandableOptions
      ref="genresList"
      v-model="search.genre"
      class="space-top-2em hidden-until-md"
      store-load-key="LeaderboardFeed/get-genre-options"
      @change="searchParamUpdated('genre')"
    />

    <div class="visible-until-sm">
      <DuiDropdown
        ref="genresDropdown"
        v-model="mobileGenre"
        class="space-top-2em"
        :disabled="isProcessing"
        label="Genre"
        name="genre"
        :nested="true"
        placeholder="Select genre"
        :search="true"
        store-load-key="LeaderboardFeed/get-genre-options"
        @option-selected="mobileOptionSelected"
      />
    </div>
  </form>
</template>

<script>
import Vue from 'vue';

import DropdownOptionsHelper from '@d-helpers/dropdown-options-helper';
import HistoryHelper from '@d-helpers/history-helper';
import UrlParamsHelper from '@d-helpers/url-params-helper';
import genresService from '@d-services/genres-service/genres-service';
import leaderboardSearchService from '@d-services/reviewer-search-service/leaderboard-search-service';
import URLS from '@d-shared/urls/urls';
import { getErrorMessage } from '@d-shared/utils/errors';

const ALL_GENRES_OPTION = {
  selected: true,
  text: 'All genres',
  value: 0,
};

let _textTagId = 0;

function _getGenreById(genres, id) {
  if (!genres || !genres.length) {
    return null;
  }

  const len = genres.length;
  let foundGenre = null;
  let i = 0;

  while (!foundGenre && (i < len)) {
    const genre = genres[i];

    if (genre.id === id) {
      foundGenre = genre;
    } else {
      foundGenre = _getGenreById(genre.options, id);
    }

    i++;
  }

  return foundGenre;
}

export default {
  data: () => ({
    isProcessing: false,
    mobileGenre: null,
    search: {
      genre: null,
      sortBy: null,
      date: null,
    },
  }),
  computed: {
    genreOptions() {
      return this.$store.getters['LeaderboardFeed/get-genre-options'];
    },
    storeSearchTags() {
      return this.$store.getters['LeaderboardFeed/ReviewerSearch/get-all-search-tags'];
    },
    dateConfig() {
      return {
        displayFormat: this.search.sortBy === 'year' ? 'YYYY' : 'MMMM YYYY',
        disabledDates: {
          from: new Date(),
          to: new Date(2019, 0, 1),
        },
        format: 'M-YYYY',
        openDate: new Date(),
        placeholder: `Choose a ${this.search.sortBy || 'month'}`,
      };
    },
  },
  watch: {
    storeSearchTags: {
      handler: function() {
        this.storeSearchTagsUpdated();
      },
      deep: true,
    },
    'search.genre': function(newGenre) {
      this.$store.dispatch('LeaderboardFeed/setSelectedGenre', newGenre);
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    addSearchTag(searchTag, performSearch = true) {
      if (performSearch) {
        this.$store.dispatch('LeaderboardFeed/ReviewerSearch/addSearchTagAndSearch', searchTag);
        return;
      }

      this.$store.dispatch('LeaderboardFeed/ReviewerSearch/addSearchTag', searchTag);
    },

    expandGenre(genreName) {
      const genre = this.genreOptions.find(g => g.text === genreName);
      if (!genre) {
        return;
      }

      this.$set(genre, 'expanded', true);
    },

    getGenre() {
      // We ignore if the update was caused by synching
      // desktop and mobile Genres search elements
      const genreTag = this.storeSearchTags.find(t => t.parentKey === 'genres');
      if (genreTag && genreTag.value === this.search.genre.value) {
        return null;
      }

      return {
        key: 'genres',
        query: 'genre_ids',
        tags: [
          {
            hidden: true,
            key: `genre-${ this.search.genre.value }`,
            option: this.search.genre,
            parentKey: 'genres',
            text: this.search.genre.text,
            value: this.search.genre.value,
          },
        ],
        value: this.search.genre.value ? [ this.search.genre.value ] : null,
      };
    },

    getSearchTag(key) {
      switch (key) {
      case 'genre':
        return this.getGenre();
      case 'sort_by':
        return this.getSortByTag(this.search.sortBy);
      case 'date':
        return this.getDateTag(this.search.date);
      default:
        return null;
      }
    },

    getSortByTag(sortByValue) {
      return {
        hidden: true,
        key: 'sort_by',
        order: true,
        value: sortByValue,
      };
    },

    getDateTag(dateValue) {
      return {
        hidden: true,
        key: 'date',
        value: dateValue,
      };
    },

    async init() {
      await this.loadGenres();
      await this.loadUrlParams();
    },

    async loadGenres() {
      try {
        const genres = await genresService.getPopularGenres(true, true);
        genres.unshift(ALL_GENRES_OPTION);
        DropdownOptionsHelper.setOptionsHrefWithBaseUrl(genres, URLS.app.leaderboard);
        this.$store.dispatch('LeaderboardFeed/setAvailableGenres', genres);
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }
    },

    initDate(date) {
      const today = new Date();
      const currentDay = today.getDate();
      // We choose previous month data for the first day of the month because we don't have anything to show to users
      const initialDate = `${currentDay === 1 ? today.getMonth() : today.getMonth() + 1}-${today.getFullYear()}`;

      this.$set(this.search, 'date', date || initialDate);
      this.searchParamUpdated('date', false);
    },

    async loadUrlParams() {
      const queryParams = UrlParamsHelper.getParamsFromCurrentUrl();
      let initialGenre = queryParams['genres'];

      if (initialGenre) {
        initialGenre = _getGenreById(this.genreOptions, initialGenre[0]);
      }

      initialGenre = initialGenre || ALL_GENRES_OPTION;

      if (!queryParams['sort_by']) {
        this.$set(this.search, 'sortBy', 'month');
      }

      if (
        !queryParams['sort_by'] ||
        (queryParams['sort_by'] && queryParams['sort_by'][0] !== 'overall')
      ) {
        this.initDate(queryParams['date'] && queryParams['date'][0]);
      }

      this.$set(this.search, 'genre', initialGenre);
      this.searchParamUpdated('genre', false);

      Object.keys(queryParams).forEach(key => {
        const currentParams = queryParams[key];

        switch (key) {
        case 'sort_by':
          this.$set(this.search, 'sortBy', currentParams[0]);
          const sortByTag = this.getSortByTag(currentParams[0]);
          this.addSearchTag(sortByTag, false);
          break;
        default:
          this.searchParamUpdated(key, currentParams[0], false);
          break;
        }
      });

      if (initialGenre === ALL_GENRES_OPTION) {
        await this.$nextTick();
        this.expandGenre('Fiction');
      }

      await leaderboardSearchService.search();
    },

    mobileOptionSelected(option) {
      this.$set(this.search, 'genre', option);
      this.searchParamUpdated('genre');
    },

    setGenre(genre) {
      this.$refs.genresList.deselectAllOptions();
      this.$set(genre, 'selected', true);
      this.$set(this, 'mobileGenre', genre.value);
    },

    removeSearchTag(searchTag) {
      this.$store.dispatch('LeaderboardFeed/ReviewerSearch/removeSearchTag', searchTag);
    },

    updateCurrentUrl() {
      let newUrl = URLS.app.leaderboard;

      const tags = {};

      const genreTag = this.storeSearchTags.find(t => t.parentKey === 'genres');
      if (genreTag) {
        tags.genres = genreTag.value;
      }

      const otherTags = this.storeSearchTags.filter(t => t.parentKey !== 'genres');

      otherTags.forEach(t => {
        tags[t.key] = t.value;
      });

      const searchParams = UrlParamsHelper.getSearchFromParams(tags);
      if (searchParams) {
        newUrl = `${ newUrl }?${ searchParams }`;
      }

      HistoryHelper.updateEntry(null, '', newUrl);
    },

    updateGenreFromTag() {
      const genreTag = this.storeSearchTags.find(t => t.parentKey === 'genres');
      if (genreTag) {
        if (genreTag.value !== this.mobileGenre) {
          this.$set(this, 'mobileGenre', genreTag.value);
        }
        return;
      }

      this.setGenre(ALL_GENRES_OPTION);
    },

    updateDateFromTag() {
      const dateTag = this.storeSearchTags.find(t => t.key === 'date');

      if (dateTag) {
        this.$set(this.search, 'date', dateTag.value);
      }
    },

    // Events
    searchParamUpdated(key, performSearch = true) {
      const searchTag = this.getSearchTag(key);
      if (!searchTag) {
        return;
      }

      if (!searchTag.value || searchTag.value === 'month') {
        this.removeSearchTag(searchTag);
        return;
      }

      this.addSearchTag(searchTag, performSearch);
    },

    storeSearchTagsUpdated() {
      this.updateGenreFromTag();
      this.updateCurrentUrl();
      this.updateDateFromTag();
    },
    // END Events
  },
};
</script>
